import React, { useMemo } from 'react';
import { Button } from 'antd';
import { InboxOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n';

export type PrivateMessageNavKey = 'inbox' | 'broadcast' | 'createBroadcast';

type PrivateMessageNavProps = {
  activeKey: PrivateMessageNavKey;
  unreadTotal: number;
  onSelect: (key: PrivateMessageNavKey) => void;
};

export function PrivateMessageNav({ activeKey, unreadTotal, onSelect }: PrivateMessageNavProps) {
  const { t } = useTranslation();
  const items = useMemo(
    () => [
      { key: 'inbox' as const, label: t('message.inbox'), icon: <InboxOutlined /> },
      { key: 'broadcast' as const, label: t('message.broadcast'), icon: <UnorderedListOutlined /> }
    ],
    [t]
  );

  return (
    <header className="flex h-14 shrink-0 items-center gap-6 border-b border-[#eef0f4] bg-white px-5">
      <nav className="flex h-full items-center gap-1" role="tablist">
        {items.map((item) => {
          const active = item.key === activeKey;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`-mb-px inline-flex h-full cursor-pointer items-center gap-1.5 border-0 border-b-2 bg-transparent px-3 text-sm transition-colors ${
                active ? 'border-[#596ae8] font-semibold text-[#3b4dd7]' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              onClick={() => onSelect(item.key)}
            >
              {item.icon}
              {item.label}
              {item.key === 'inbox' && unreadTotal > 0 ? (
                <span className="min-w-[18px] rounded-full bg-[#596ae8] px-1.5 text-center text-[11px] font-semibold leading-[18px] text-white">
                  {unreadTotal > 99 ? '99+' : unreadTotal}
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className="ml-auto flex items-center gap-5">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="wb-btn-primary rounded-lg"
          onClick={() => onSelect('createBroadcast')}
        >
          {t('message.create_broadcast')}
        </Button>
      </div>
    </header>
  );
}
